import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CommonTable from '../../../../components/common-table/CommonTable';
import UsersTableItem from './UsersTableItem';
import Tr from '../../../../components/common-table/components/Tr';
import Th from '../../../../components/common-table/components/Th';
import ConfirmationModal from '../../../../components/confirmation-modal/ConfirmationModal';
import DeleteLabel from '../../../../projects/projects-list/components/DeleteLabel';
import UserFormModal from '../user-form-modal/UserFormModal';
import { MODAL_TYPES } from '../../../../constants';
import { customerEditUserFormYopValidation } from '../user-form-modal/validation';
import {
  enableProjectToCompanyUser,
  enableUser,
  removeUserFromCompany,
} from '../../../../redux/customers/customersActions';
import AssignProjectsModal from '../../../customer-create/company-users/assign-projects-modal/AssignProjectsModal';

import scss from './UsersTable.module.scss';

const UsersTable = () => {
  const { t } = useTranslation('customers');
  const dispatch = useDispatch();

  const { id: companyId } = useParams();

  const users = useSelector((state) => state.customers.customerInfo.customers) || [];
  const companyProjects = useSelector((state) => state.customers.customerInfo.companyProjects) || [];
  const isCustomerInfoLoading = useSelector((state) => state.customers.isCustomerInfoLoading);

  const [selectedUser, setSelectedUser] = useState({});

  const formattedCompanyProjects = companyProjects.map(({ projectId, projectName, enabled }) => ({
    id: projectId,
    name: projectName,
    enabled,
  }));

  const handleCloseModals = useCallback(() => {
    setSelectedUser({});
  }, []);

  const handleOpenDeleteModal = useCallback((customer) => {
    setSelectedUser({ ...customer, modalType: MODAL_TYPES.DELETE });
  }, []);

  const handleOpenEditModal = useCallback((customer) => {
    setSelectedUser({ ...customer, modalType: MODAL_TYPES.EDIT });
  }, []);

  const handleEditUser = useCallback(async (values) => {
    setSelectedUser({});
  }, []);

  const handleDeleteUser = useCallback(() => {
    dispatch(removeUserFromCompany({ userId: selectedUser.id, companyId }));
  }, [dispatch, selectedUser, companyId]);

  const handleEnableUser = useCallback(
    ({ id, enabled }) => {
      dispatch(enableUser({ id, enabled, shouldUpdateCustomerInfo: true }));
    },
    [dispatch],
  );

  const handleOpenChangeProjectsModal = (user) => {
    setSelectedUser(user);
  };

  const handleChangeUserProjects = ({ id, checked }) => {
    dispatch(enableProjectToCompanyUser({ projectId: id, userId: selectedUser.id, enabled: checked, companyId }));
  };

  return (
    <div className={scss.tableWrapper}>
      <CommonTable
        content={users || []}
        isLoading={isCustomerInfoLoading}
        renderBody={(item) => (
          <UsersTableItem
            key={item.id}
            user={item}
            onDelete={handleOpenDeleteModal}
            onEdit={handleOpenEditModal}
            onEnable={handleEnableUser}
            onChangeUserProjects={handleOpenChangeProjectsModal}
          />
        )}>
        <Tr>
          <Th isLeft>{t('user')}</Th>
          <Th isLeft>{t('fullName')}</Th>
          <Th isLeft extraClasses={scss.cellProjects}>
            {t('projects')}
          </Th>
          <Th extraClasses={scss.cellEnable}>{t('enabled')}</Th>
          <Th isLeft>
            <span>
              {t('activated')} {' / '}
              <span className={scss.cellDeactivated}>{t('deactivated')}</span> {t('by')}
            </span>
          </Th>
          <Th isLeft extraClasses={scss.cellAction}>
            {t('actions')}
          </Th>
        </Tr>
      </CommonTable>

      <ConfirmationModal
        isOpen={selectedUser.modalType === MODAL_TYPES.DELETE}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteUser}
        subLabel={<DeleteLabel name={selectedUser.username} />}
      />

      <UserFormModal
        isOpen={selectedUser.modalType === MODAL_TYPES.EDIT}
        user={selectedUser}
        onSubmit={handleEditUser}
        onCancel={handleCloseModals}
        onClose={handleCloseModals}
        formTitle={t('editUser')}
        submitButtonText={t('edit')}
        validation={customerEditUserFormYopValidation}
      />

      <AssignProjectsModal
        isOpen={selectedUser.modalType === 'CHANGE_PROJECT'}
        companyProjects={formattedCompanyProjects}
        activeProjects={selectedUser.assignedProjects}
        onSelect={handleChangeUserProjects}
        onClose={handleCloseModals}
      />
    </div>
  );
};

export default UsersTable;
